
import { Button, Pagination, Table, Flex, Select, Space, Tag, message, DatePicker, Switch, Typography, Avatar, Input, Checkbox } from "antd";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import { RichText } from "@chatui/core";
import { useEffect, useState } from "react";
import { fetchEconomicEvents, resendEventsNotification, updateEconomicEvents } from "../../api/ai";
import { ArrowDownOutlined, ArrowUpOutlined, EyeFilled } from "@ant-design/icons";
import EventsContentReview from "./events_content_review";
import { conversionUtcDate, formatWithDecimal } from "../../utils/comm";

import { countryTuples } from 'country-region-data';

const EconomicEventsView = () => {
    const countries = countryTuples.map(item => {
        return {
            label: item[0].split(',')[0],
            value: item[0],
            flag: item[1]
        }
    })
    const tabCols = [
        {
            title: 'Index',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, _, index) => {
                return (pageNum - 1) * pageSize + (index + 1);
            },
        },
        {
            title: 'Event Name',
            dataIndex: 'eventName',
            key: 'eventName',
            render: (text, _) => {
                return <strong>{text}</strong>;
            },
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            render: (text, _) => {
                return <Tag>{text}</Tag>;
            },
        },
        {
            title: 'Date',
            dataIndex: 'dateTime',
            key: 'dateTime',
            render: (text, _) => {
                return <label style={{ fontSize: 12, color: '#afafaf', whiteSpace: 'nowrap' }}>{dayjs(text).format('MM-DD HH:mm')}</label>
            },
        },
        {
            title: 'Previous',
            dataIndex: 'previous',
            key: 'previous',
        },
        {
            title: 'Forecast',
            dataIndex: 'forecast',
            key: 'forecast',
        },
        {
            title: 'Actual',
            dataIndex: 'actual',
            key: 'actual',
            render: (text, record) => {
                if (!text) return '';
                let actual = text.replaceAll('%', '').replaceAll(',', '').replaceAll('B', '').replaceAll('M', '').replaceAll('K', '').replaceAll('T', '');
                let previous = record.previous.replaceAll('%', '').replaceAll(',', '').replaceAll('B', '').replaceAll('M', '').replaceAll('K', '').replaceAll('T', '');
                let isUp = parseFloat(actual) > parseFloat(previous);
                return <Space size={[6, 0]}>
                    <span>{formatWithDecimal(text)}</span>
                    {
                        isUp ? <ArrowUpOutlined style={{ color: 'green', fontSize: 14 }} /> : <ArrowDownOutlined style={{ color: 'red', fontSize: 14 }} />
                    }
                </Space>
            },
        },
        {
            title: 'Summary',
            dataIndex: 'summary',
            key: 'summary',
            render: (_, record) => {
                try {
                    let summary = record.summary;
                    if (!summary) return '';
                    summary = JSON.parse(summary);
                    if (!summary.article_body) return '';
                    return (
                        <Space>
                            <div style={{ background: '#f1f1f1', padding: '8px 12px', borderRadius: 8, width: 160 }}>
                                <div className="community-content">
                                    <RichText content={summary.article_body} />
                                </div>
                            </div>
                            <EyeFilled style={{ color: 'grey', cursor: 'pointer' }} onClick={() => reviewHandle(record, 1)} />
                        </Space>
                    )
                } catch (error) {
                    return ''
                }
            },
        },
        {
            title: 'Notification',
            dataIndex: 'notification',
            key: 'notification',
            render: (_, record) => {
                try {
                    let summary = record.summary;
                    if (!summary) return '';
                    summary = JSON.parse(summary);
                    if (!summary.notification_body) return '';
                    return (
                        <Space>
                            <div style={{ background: '#f1f1f1', padding: '8px 12px', borderRadius: 8, width: 160 }}>
                                <div className="community-content">
                                    <RichText content={summary.notification_body} />
                                </div>
                            </div>
                            <EyeFilled style={{ color: 'grey', cursor: 'pointer' }} onClick={() => reviewHandle(record, 2)} />
                        </Space>
                    )
                } catch (error) {
                    return ''
                }
            },
        },
        {
            title: 'Event Status',
            dataIndex: 'eventStatus',
            key: 'eventStatus',
            render: (text, record) => {
                if (text === 0) {
                    return <Tag color="blue" style={{ fontSize: 12 }}>PENDING</Tag>;
                } else if (text === 1) {
                    const { eventAction } = record;
                    if (eventAction === 1) {
                        return <Tag color="green" style={{ fontSize: 12 }}>SENT</Tag>;
                    }
                }
            },
        },
        {
            title: 'Notification Status',
            dataIndex: 'notificationStatus',
            key: 'notificationStatus',
            render: (text, record) => {
                const { eventAction, notificationAction } = record;
                if (eventAction !== 1) return '';
                if (notificationAction !== 1) return '';
                if (text === 0) {
                    return <Tag color="blue" style={{ fontSize: 12 }}>PENDING</Tag>;
                } else if (text === 1) {
                    return <Tag color="green" style={{ fontSize: 12 }}>PUSHED</Tag>
                }
            },
        },
        {
            title: 'Event Action',
            dataIndex: 'eventAction',
            key: 'eventAction',
            fixed: 'right',
            render: (text, record) => {
                const disabled = record.eventStatus !== 0;
                return <Switch disabled={disabled} value={text === 1} checkedChildren='ON' unCheckedChildren='OFF' onChange={(value) => updateEventAction(record, value)} />
            },
        },
        {
            title: 'Notification Action',
            dataIndex: 'notificationAction',
            key: 'notificationAction',
            fixed: 'right',
            render: (text, record) => {
                const { eventAction, eventStatus, notificationStatus } = record;

                if (eventAction !== 1) return '';
                const disabled = notificationStatus !== 0;
                return (
                    <Space>
                        {
                            eventStatus !== 1 && <Switch disabled={disabled} value={text === 1} checkedChildren='ON' unCheckedChildren='OFF' onChange={(value) => updateNotificationAction(record, value)} />
                        }
                        {
                            (eventStatus === 1 && notificationStatus !== 1) && <Button size='small' onClick={() => resendHandle(record)}>Push</Button>
                        }
                    </Space>
                )
            },
        },
        {
            title: 'ShowAt Home',
            dataIndex: 'isShowAtHome',
            key: 'isShowAtHome',
            fixed: 'right',
            render: (text, record) => {
                return <Checkbox checked={text} onChange={(e) => updateShowAtHome(record, e.target.checked)} />
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            fixed: 'right',
            render: (text, record, index) => {
                if(!record.isShowAtHome) return '';
                return (
                    <Space size='small'>
                        <Button type="link" style={{padding: 0, fontSize: 12}} onClick={() => retryHandle(record)}>Analysis Event Retry</Button>
                    </Space>
                )
            },
        },
    ];

    const [searchParams, setSearchParams] = useState({
        eventStatus: undefined,
        notificationStatus: undefined,
        country: undefined,
        dateTime: undefined,
        eventName: undefined,
        isShowAtHome: undefined,
    });
    const [tabData, setTabData] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currEvents, setCurrEvents] = useState();
    const [previewType, setPreviewType] = useState();
    const [previewShow, setPreviewShow] = useState(false);

    useEffect(() => {
        fetchData(pageNum, pageSize, searchParams);
    }, [])

    const fetchData = useDebouncedCallback((page, pagesize, search) => {
        setLoading(true);
        let params = {
            'page': page,
            'pageSize': pagesize,
            ...search,
        };
        fetchEconomicEvents(params).then(res => {
            if (res.pagination) {
                setTotal(res.pagination.totalCount);
            }
            if (res.events) {
                setTabData(res.events);
            }
        }).finally(() => setLoading(false))
    }, 300)

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        fetchData(page, size, searchParams);
    }

    const onSearchChange = (tag, value) => {
        let newParams = { ...searchParams };
        newParams[tag] = value;

        setSearchParams(newParams);
        setPageNum(1);
        fetchData(1, pageSize, newParams);
    }

    const updateEventAction = (record, value) => {
        const eventAction = value ? 1 : 0;
        let params = {
            eventId: record.id,
            eventAction: eventAction,
        }
        if (eventAction === 0) {
            params.notificationAction = 0;
            record.notificationAction = 0;
        }
        record.eventAction = eventAction;
        setTabData([...tabData]);

        setLoading(true);
        updateEconomicEvents(params).then(res => {
            if (!res) {
                message.error('Failed to update event action');
            }
        }).finally(() => {
            fetchData(pageNum, pageSize, searchParams);
        });
    }

    const updateShowAtHome = (record, value) => {
        let params = {
            eventId: record.id,
            isShowAtHome: value,
        }
        record.isShowAtHome = value;
        setTabData([...tabData]);
        setLoading(true);
        updateEconomicEvents(params).then(res => {
            if (!res) {
                message.error('Failed to update show at home');
            }
        }).finally(() => {
            fetchData(pageNum, pageSize, searchParams);
        })
    }

    const retryHandle = (record) => {
        let params = {
            eventId: record.id,
            isShowAtHomeRetryQuery: true,
        }

        setLoading(true);
        updateEconomicEvents(params).then(res => {
            if (res) {
                message.success('Retry success!');
            }
        }).finally(() => setLoading(false));
    }

    const updateNotificationAction = (record, value) => {
        const notificationAction = value ? 1 : 0;
        let params = {
            eventId: record.id,
            notificationAction: notificationAction,
        }
        record.notificationAction = notificationAction;
        setTabData([...tabData]);

        setLoading(true);
        updateEconomicEvents(params).then(res => {
            if (res) {
                fetchData(pageNum, pageSize, searchParams);
            } else {
                setLoading(false);
            }
        });
    }

    const reviewHandle = (record, type) => {
        setCurrEvents(record);
        setPreviewType(type);
        setPreviewShow(true);
    }

    const resendHandle = (record) => {
        let params = {
            economicEventId: record.id
        };
        setLoading(true);
        resendEventsNotification(params).then(res => {
            if (res) {
                fetchData(pageNum, pageSize, searchParams);
            } else {
                setLoading(false);
            }
        });
    }

    return (
        <div>
            <div className='top-part cnt-item'>
                <Flex justify="space-between">
                    <Space wrap style={{ justifyContent: 'flex-end' }}>
                        <Select
                            style={{ width: 160 }}
                            options={[
                                { key: 1, label: 'Pending', value: '0' },
                                { key: 2, label: 'Sent', value: '1' },
                            ]}
                            placeholder='Event status'
                            onChange={value => onSearchChange('eventStatus', value)}
                            allowClear
                        />
                        <Select
                            style={{ width: 160 }}
                            options={[
                                { key: 1, label: 'YES', value: true },
                                { key: 2, label: 'NO', value: false },
                            ]}
                            placeholder='Show At Home'
                            onChange={value => onSearchChange('isShowAtHome', value)}
                            allowClear
                        />
                        <Select
                            style={{ width: 220 }}
                            placeholder="country/city select..."
                            options={countries}
                            optionRender={(option) => {
                                let flag = `http://purecatamphetamine.github.io/country-flag-icons/3x2/${option.data.flag}.svg`;
                                return (
                                    <Space size={[4, 0]}>
                                        <Avatar src={flag} size={16} />
                                        <Typography.Text style={{ fontSize: 12 }}>
                                            <b>{option.data.label}</b>
                                        </Typography.Text>
                                    </Space>
                                )
                            }}
                            onChange={val => onSearchChange('country', val)}
                            allowClear
                            showSearch />

                        <DatePicker
                            format={(value) => `${value ? conversionUtcDate(value).split(' ')[0] : ''}`}
                            placeholder="DateTime"
                            onChange={(_, date) => onSearchChange('dateTime', date)}
                        />
                        <Input
                            placeholder="event search"
                            allowClear
                            onChange={evt => onSearchChange('eventName', evt.target.value)}
                        />
                    </Space>
                </Flex>
            </div>
            <div className='cnt-item'>
                <Table
                    scroll={{ x: true }}
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowClassName={'table-row'}
                    size='small'
                    dataSource={tabData}
                    columns={tabCols}
                    pagination={false}
                />
            </div>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>

            <EventsContentReview
                events={currEvents}
                open={previewShow}
                type={previewType}
                onClose={() => setPreviewShow(false)}
            />
        </div>
    )
}

export default EconomicEventsView;